import { useState } from "react";
import type { CharacterState, Pact, Rules } from "@/data/types";
import { calcDerivedStats } from "@/lib/characterCalc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Lock,
  AlertTriangle,
  Dice5,
} from "lucide-react";

export function StepOtherworldlyGifts({
  state,
  rules,
  stats,
  onChange,
}: {
  state: CharacterState;
  rules: Rules;
  stats: ReturnType<typeof calcDerivedStats>;
  onChange: (s: CharacterState) => void;
}) {
  const allPacts = [...state.pacts, ...(state.bonusPacts || [])];

  // Which pact indices have their gift grid expanded.
  // Default: expanded when no gift is selected, collapsed when one is.
  const [expanded, setExpanded] = useState<Set<number>>(() => {
    const s = new Set<number>();
    allPacts.forEach((p, i) => {
      if (!p.giftId) s.add(i);
    });
    return s;
  });

  // Which pact indices have their skill picker grid expanded.
  // Default: expanded when no giftSkillId, collapsed when one is chosen.
  const [skillExpanded, setSkillExpanded] = useState<Set<number>>(() => {
    const s = new Set<number>();
    allPacts.forEach((p, i) => {
      if (p.giftId === "gift-of-earthly-experience" && !p.giftSkillId) s.add(i);
    });
    return s;
  });

  // Which pact indices have their offering-swap picker open.
  // Default: open when gift-of-choice but swap is incomplete.
  const [choiceExpanded, setChoiceExpanded] = useState<Set<number>>(() => {
    const s = new Set<number>();
    allPacts.forEach((p, i) => {
      if (
        p.giftId === "gift-of-choice" &&
        (!p.choiceSwapFromId || !p.choiceSwapToId)
      )
        s.add(i);
    });
    return s;
  });

  const toggleChoiceExpanded = (absIdx: number) =>
    setChoiceExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(absIdx)) next.delete(absIdx);
      else next.add(absIdx);
      return next;
    });

  const toggleExpanded = (absIdx: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(absIdx)) next.delete(absIdx);
      else next.add(absIdx);
      return next;
    });

  const toggleSkillExpanded = (absIdx: number) =>
    setSkillExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(absIdx)) next.delete(absIdx);
      else next.add(absIdx);
      return next;
    });

  const updateGift = (
    pactIdx: number,
    giftId: string,
    isBonusPact: boolean,
    absIdx: number
  ) => {
    // Clear giftSkillId and choiceSwap fields whenever the gift changes (including deselect)
    const updatedPact = (p: Pact, i: number) =>
      i === pactIdx
        ? {
            ...p,
            giftId,
            giftSkillId: undefined,
            choiceSwapFromId: undefined,
            choiceSwapToId: undefined,
          }
        : p;

    if (isBonusPact) {
      onChange({
        ...state,
        bonusPacts: (state.bonusPacts || []).map(updatedPact),
      });
    } else {
      onChange({ ...state, pacts: state.pacts.map(updatedPact) });
    }
    // Selecting a gift collapses the list; deselecting expands it
    setExpanded((prev) => {
      const next = new Set(prev);
      if (giftId) next.delete(absIdx);
      else next.add(absIdx);
      return next;
    });
    // When switching TO earthly experience, open the skill picker; otherwise close it
    setSkillExpanded((prev) => {
      const next = new Set(prev);
      if (giftId === "gift-of-earthly-experience") next.add(absIdx);
      else next.delete(absIdx);
      return next;
    });
    // When switching TO gift-of-choice, open the choice picker; otherwise close it
    setChoiceExpanded((prev) => {
      const next = new Set(prev);
      if (giftId === "gift-of-choice") next.add(absIdx);
      else next.delete(absIdx);
      return next;
    });
  };

  const updateGiftSkill = (
    pactIdx: number,
    skillId: string,
    isBonusPact: boolean,
    absIdx: number
  ) => {
    const updatedPact = (p: Pact, i: number) =>
      i === pactIdx
        ? { ...p, giftSkillId: p.giftSkillId === skillId ? undefined : skillId }
        : p;

    if (isBonusPact) {
      onChange({
        ...state,
        bonusPacts: (state.bonusPacts || []).map(updatedPact),
      });
    } else {
      onChange({ ...state, pacts: state.pacts.map(updatedPact) });
    }
    // Collapse the skill picker once a skill is chosen; re-open if deselecting
    setSkillExpanded((prev) => {
      const next = new Set(prev);
      const currentPact = isBonusPact
        ? (state.bonusPacts || [])[pactIdx]
        : state.pacts[pactIdx];
      const isDeselecting = currentPact?.giftSkillId === skillId;
      if (isDeselecting) next.add(absIdx);
      else next.delete(absIdx);
      return next;
    });
  };

  const usedLimitedGiftIds = [
    ...allPacts.map((p) => p.giftId),
    ...(state.standaloneGifts || []).map((sg) => sg.giftId),
  ].filter((id) => {
    const gift = rules.otherworldlyGifts.find((g) => g.id === id);
    return gift && !gift.unlimited;
  });

  // Offering IDs already claimed as the swap-from by another Gift of Choice pact.
  const usedSwapFromIds = allPacts
    .filter((p) => p.giftId === "gift-of-choice" && p.choiceSwapFromId)
    .map((p) => p.choiceSwapFromId!);

  const randomiseGift = (
    actualIdx: number,
    isBonusPact: boolean,
    pactIdx: number,
    currentGiftId: string | undefined
  ) => {
    const available = rules.otherworldlyGifts.filter((gift) => {
      if (gift.id === currentGiftId) return false;
      if (!gift.unlimited && usedLimitedGiftIds.includes(gift.id)) return false;
      return true;
    });
    if (available.length === 0) return;
    // eslint-disable-next-line react-hooks/purity
    const pick = available[Math.floor(Math.random() * available.length)];

    let giftSkillId: string | undefined;
    let choiceSwapFromId: number | undefined;
    let choiceSwapToId: number | undefined;

    if (pick.id === "gift-of-earthly-experience") {
      // Eligible core skills: non-repeatable ones not claimed by OTHER pacts or core skill step
      const otherPactSkillIds = allPacts
        .filter(
          (p, i) =>
            i !== pactIdx &&
            p.giftId === "gift-of-earthly-experience" &&
            p.giftSkillId
        )
        .map((p) => p.giftSkillId!);
      const coreStepNonRepeatableIds = state.coreSkillSelections
        .map((s) => s.skillId)
        .filter((id) => {
          const skill = rules.coreSkills.find((s) => s.id === id);
          return skill && !skill.repeatable;
        });
      const blockedSkillIds = new Set([
        ...otherPactSkillIds,
        ...coreStepNonRepeatableIds,
      ]);
      const eligibleSkills = rules.coreSkills.filter((skill) => {
        if (!skill.repeatable && blockedSkillIds.has(skill.id)) return false;
        return true;
      });
      if (eligibleSkills.length > 0) {
        // eslint-disable-next-line react-hooks/purity
        const idx = Math.floor(Math.random() * eligibleSkills.length);
        giftSkillId = eligibleSkills[idx].id;
      }
    }

    if (pick.id === "gift-of-choice") {
      // swapFrom: one of the other pacts' offering IDs, not already used by another gift-of-choice
      const otherPactOfferingIds = allPacts
        .filter((_, i) => i !== pactIdx)
        .map((p) => p.darkOfferingId)
        .filter((id): id is number => !!id);
      const otherUsedSwapFromIds = allPacts
        .filter(
          (p, i) =>
            i !== pactIdx && p.giftId === "gift-of-choice" && p.choiceSwapFromId
        )
        .map((p) => p.choiceSwapFromId!);
      const availableSwapFrom = otherPactOfferingIds.filter(
        (oid) => !otherUsedSwapFromIds.includes(oid)
      );
      if (availableSwapFrom.length > 0) {
        // eslint-disable-next-line react-hooks/purity
        const fromIdx = Math.floor(Math.random() * availableSwapFrom.length);
        choiceSwapFromId = availableSwapFrom[fromIdx];
        const swapToOptions = rules.darkOfferings.filter(
          (o) => o.id !== choiceSwapFromId
        );
        if (swapToOptions.length > 0) {
          // eslint-disable-next-line react-hooks/purity
          const toIdx = Math.floor(Math.random() * swapToOptions.length);
          choiceSwapToId = swapToOptions[toIdx].id;
        }
      }
    }

    const updatedPact = (p: Pact, i: number) =>
      i !== actualIdx
        ? p
        : {
            ...p,
            giftId: pick.id,
            giftSkillId,
            choiceSwapFromId,
            choiceSwapToId,
          };

    if (isBonusPact) {
      onChange({
        ...state,
        bonusPacts: (state.bonusPacts || []).map(updatedPact),
      });
    } else {
      onChange({ ...state, pacts: state.pacts.map(updatedPact) });
    }

    // Collapse all sub-pickers since they're fully populated
    setExpanded((prev) => {
      const next = new Set(prev);
      next.delete(pactIdx);
      return next;
    });
    setSkillExpanded((prev) => {
      const next = new Set(prev);
      next.delete(pactIdx);
      return next;
    });
    setChoiceExpanded((prev) => {
      const next = new Set(prev);
      next.delete(pactIdx);
      return next;
    });
  };

  const updateChoiceSwapFrom = (
    pactIdx: number,
    offeringId: number,
    isBonusPact: boolean
  ) => {
    const updatedPact = (p: Pact, i: number) => {
      if (i !== pactIdx) return p;
      const isDeselecting = p.choiceSwapFromId === offeringId;
      // Changing the from-offering clears the to-offering
      return {
        ...p,
        choiceSwapFromId: isDeselecting ? undefined : offeringId,
        choiceSwapToId: undefined,
      };
    };
    if (isBonusPact) {
      onChange({
        ...state,
        bonusPacts: (state.bonusPacts || []).map(updatedPact),
      });
    } else {
      onChange({ ...state, pacts: state.pacts.map(updatedPact) });
    }
  };

  const updateChoiceSwapTo = (
    pactIdx: number,
    offeringId: number,
    isBonusPact: boolean,
    absIdx: number
  ) => {
    const currentPact = isBonusPact
      ? (state.bonusPacts || [])[pactIdx]
      : state.pacts[pactIdx];
    const isDeselecting = currentPact?.choiceSwapToId === offeringId;
    const updatedPact = (p: Pact, i: number) =>
      i === pactIdx
        ? { ...p, choiceSwapToId: isDeselecting ? undefined : offeringId }
        : p;
    if (isBonusPact) {
      onChange({
        ...state,
        bonusPacts: (state.bonusPacts || []).map(updatedPact),
      });
    } else {
      onChange({ ...state, pacts: state.pacts.map(updatedPact) });
    }
    // Collapse when a selection is made; re-open if deselecting
    setChoiceExpanded((prev) => {
      const next = new Set(prev);
      if (isDeselecting) next.add(absIdx);
      else next.delete(absIdx);
      return next;
    });
  };

  // Non-repeatable core skills that are off-limits for Gift of Earthly Experience:
  // either already chosen by another pact's gift, or already selected in the normal Core Skills step.
  const usedNonRepeatableGiftSkillIds = [
    // From other Gift of Earthly Experience pacts
    ...allPacts
      .filter((p) => p.giftId === "gift-of-earthly-experience" && p.giftSkillId)
      .map((p) => p.giftSkillId!)
      .filter((id) => {
        const skill = rules.coreSkills.find((s) => s.id === id);
        return skill && !skill.repeatable;
      }),
    // From normal Core Skills selections
    ...state.coreSkillSelections
      .map((s) => s.skillId)
      .filter((id) => {
        const skill = rules.coreSkills.find((s) => s.id === id);
        return skill && !skill.repeatable;
      }),
  ];

  if (allPacts.length === 0) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-bold mb-1">Otherworldly Gifts</h2>
          <p className="text-muted-foreground text-sm">
            For each pact, choose an Otherworldly Gift granted by your patron.
          </p>
        </div>
        <div className="text-center py-8 text-muted-foreground text-sm border-2 border-dashed rounded-lg">
          You need to create pacts in the previous step before selecting gifts.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold mb-1">Otherworldly Gifts</h2>
        <p className="text-muted-foreground text-sm">
          For each pact, choose an Otherworldly Gift. Unlimited gifts may be
          chosen multiple times; limited gifts may only be chosen once total.
        </p>
      </div>

      {allPacts.map((pact, pactIdx) => {
        const isBonusPact = pactIdx >= state.pacts.length;
        const actualIdx = isBonusPact ? pactIdx - state.pacts.length : pactIdx;
        const patron = rules.patrons.find((p) => p.id === pact.patronId);
        const offering = rules.darkOfferings.find(
          (o) => o.id === pact.darkOfferingId
        );
        const selectedGift = rules.otherworldlyGifts.find(
          (g) => g.id === pact.giftId
        );
        const isExpanded = expanded.has(pactIdx);
        const isSkillExpanded = skillExpanded.has(pactIdx);
        const isChoiceExpanded = choiceExpanded.has(pactIdx);
        const selectedGiftSkill = rules.coreSkills.find(
          (s) => s.id === pact.giftSkillId
        );
        const swapFromOffering = rules.darkOfferings.find(
          (o) => o.id === pact.choiceSwapFromId
        );
        const swapToOffering = rules.darkOfferings.find(
          (o) => o.id === pact.choiceSwapToId
        );
        // Offerings from OTHER pacts available to swap out (this pact's own offering excluded)
        const otherPactOfferingIds = allPacts
          .filter((_, i) => i !== pactIdx)
          .map((p) => p.darkOfferingId)
          .filter(Boolean);

        return (
          <Card key={pactIdx}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-sm">
                  Pact {pactIdx + 1} Gift
                  {isBonusPact && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      Bonus
                    </Badge>
                  )}
                </CardTitle>
                {patron && (
                  <Badge variant="outline" className="text-xs">
                    {patron.name}
                  </Badge>
                )}
                {offering && (
                  <Badge variant="destructive" className="text-xs">
                    {offering.name}
                  </Badge>
                )}
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              {/* ── SELECTED GIFT SUMMARY (when collapsed) ── */}
              {selectedGift && !isExpanded && (
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2 rounded-md border border-primary bg-primary/10 p-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">
                          {selectedGift.name}
                        </span>
                        {selectedGift.unlimited && (
                          <Badge variant="secondary" className="text-xs">
                            Unlimited
                          </Badge>
                        )}
                        <Check className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                        {selectedGift.description}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs shrink-0"
                      onClick={() => toggleExpanded(pactIdx)}
                    >
                      <ChevronDown className="h-3 w-3 mr-1" />
                      Change
                    </Button>
                  </div>

                  {/* ── INLINE SKILL PICKER for Gift of Earthly Experience ── */}
                  {selectedGift.id === "gift-of-earthly-experience" && (
                    <div className="rounded-md border border-dashed border-amber-500/60 bg-amber-500/5 p-3 space-y-2">
                      <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                        Core Skill from this gift:
                      </p>

                      {/* Collapsed: show chosen skill summary + Change button */}
                      {selectedGiftSkill && !isSkillExpanded && (
                        <div className="flex items-start justify-between gap-2 rounded border border-amber-500/60 bg-amber-500/10 p-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-medium text-xs">
                                {selectedGiftSkill.name}
                              </span>
                              {selectedGiftSkill.repeatable && (
                                <Badge
                                  variant="secondary"
                                  className="text-xs py-0"
                                >
                                  Repeatable
                                </Badge>
                              )}
                              <Check className="h-3 w-3 text-amber-500" />
                            </div>
                            <p className="text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-2">
                              {selectedGiftSkill.description}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs shrink-0"
                            onClick={() => toggleSkillExpanded(pactIdx)}
                          >
                            <ChevronDown className="h-3 w-3 mr-1" />
                            Change
                          </Button>
                        </div>
                      )}

                      {/* No skill yet — prompt */}
                      {!selectedGiftSkill && !isSkillExpanded && (
                        <p className="text-xs text-muted-foreground italic">
                          No skill selected — pick one below.
                        </p>
                      )}

                      {/* Expanded: full skill grid */}
                      {isSkillExpanded && (
                        <div className="space-y-1.5">
                          {selectedGiftSkill && (
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-muted-foreground">
                                Currently:{" "}
                                <span className="font-medium text-foreground">
                                  {selectedGiftSkill.name}
                                </span>
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={() => toggleSkillExpanded(pactIdx)}
                              >
                                <ChevronUp className="h-3 w-3 mr-1" />
                                Collapse
                              </Button>
                            </div>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {rules.coreSkills.map((skill) => {
                              const isSkillSelected =
                                pact.giftSkillId === skill.id;
                              const atCap =
                                (skill.id === "endurance-training" &&
                                  stats.hp >= rules.limits.maxHp) ||
                                (skill.id === "inherited-wealth" &&
                                  stats.wealthLevel >=
                                    rules.limits.maxWealthFromSkills);
                              const skillLocked = isSkillSelected
                                ? false
                                : atCap ||
                                  (!skill.repeatable &&
                                    usedNonRepeatableGiftSkillIds.includes(
                                      skill.id
                                    ));
                              const capReason =
                                skill.id === "endurance-training" && atCap
                                  ? `HP at max (${rules.limits.maxHp})`
                                  : skill.id === "inherited-wealth" && atCap
                                    ? `Wealth at max (${rules.wealthLevels[rules.limits.maxWealthFromSkills]?.name})`
                                    : null;
                              return (
                                <button
                                  key={skill.id}
                                  disabled={skillLocked}
                                  onClick={() =>
                                    updateGiftSkill(
                                      actualIdx,
                                      skill.id,
                                      isBonusPact,
                                      pactIdx
                                    )
                                  }
                                  className={`text-left rounded border p-2 transition-colors w-full ${
                                    isSkillSelected
                                      ? "border-amber-500 bg-amber-500/15"
                                      : skillLocked
                                        ? "opacity-35 cursor-not-allowed bg-muted/20"
                                        : "hover:border-amber-500/50 hover:bg-amber-500/5 cursor-pointer"
                                  }`}
                                >
                                  <div className="flex items-start gap-1.5">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="font-medium text-xs">
                                          {skill.name}
                                        </span>
                                        {skill.repeatable && (
                                          <Badge
                                            variant="secondary"
                                            className="text-xs py-0"
                                          >
                                            Repeatable
                                          </Badge>
                                        )}
                                        {skillLocked && !capReason && (
                                          <Lock className="h-3 w-3 text-muted-foreground" />
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-2">
                                        {skill.description}
                                      </p>
                                      {capReason && (
                                        <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5 flex items-center gap-1">
                                          <AlertTriangle className="h-2.5 w-2.5" />
                                          {capReason}
                                        </p>
                                      )}
                                    </div>
                                    {isSkillSelected && (
                                      <Check className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── INLINE SWAP PICKER for Gift of Choice ── */}
                  {selectedGift.id === "gift-of-choice" && (
                    <div className="rounded-md border border-dashed border-violet-500/60 bg-violet-500/5 p-3 space-y-2">
                      <p className="text-xs font-semibold text-violet-600 dark:text-violet-400">
                        Offering swap from this gift:
                      </p>

                      {/* Collapsed: show completed swap summary + Change button */}
                      {swapFromOffering &&
                        swapToOffering &&
                        !isChoiceExpanded && (
                          <div className="flex items-center justify-between gap-2 rounded border border-violet-500/60 bg-violet-500/10 p-2">
                            <div className="flex items-center gap-2 flex-wrap text-xs">
                              <span className="font-medium line-through text-muted-foreground">
                                {swapFromOffering.name}
                              </span>
                              <span className="text-muted-foreground">→</span>
                              <span className="font-medium">
                                {swapToOffering.name}
                              </span>
                              <Check className="h-3 w-3 text-violet-500" />
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs shrink-0"
                              onClick={() => toggleChoiceExpanded(pactIdx)}
                            >
                              <ChevronDown className="h-3 w-3 mr-1" />
                              Change
                            </Button>
                          </div>
                        )}

                      {/* No swap yet — prompt */}
                      {!swapFromOffering && !isChoiceExpanded && (
                        <p className="text-xs text-muted-foreground italic">
                          No swap configured — select one below.
                        </p>
                      )}

                      {/* Expanded: two-step picker */}
                      {isChoiceExpanded && (
                        <div className="space-y-2">
                          {/* Header row with collapse button when already have some data */}
                          {(swapFromOffering || swapToOffering) && (
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-muted-foreground">
                                {swapFromOffering && !swapToOffering ? (
                                  <>
                                    Replacing:{" "}
                                    <span className="font-medium text-foreground">
                                      {swapFromOffering.name}
                                    </span>{" "}
                                    — now choose the new offering
                                  </>
                                ) : (
                                  "Choose offering to replace:"
                                )}
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={() => toggleChoiceExpanded(pactIdx)}
                              >
                                <ChevronUp className="h-3 w-3 mr-1" />
                                Collapse
                              </Button>
                            </div>
                          )}

                          {/* Step 1: pick which offering to remove */}
                          {!swapFromOffering && (
                            <>
                              <p className="text-xs text-muted-foreground">
                                Select which offering to replace:
                              </p>
                              {otherPactOfferingIds.length === 0 && (
                                <p className="text-xs italic text-muted-foreground">
                                  No other offerings available to swap.
                                </p>
                              )}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                {otherPactOfferingIds.map((oid) => {
                                  const off = rules.darkOfferings.find(
                                    (o) => o.id === oid
                                  );
                                  if (!off) return null;
                                  const alreadyUsed =
                                    usedSwapFromIds.includes(oid) &&
                                    oid !== pact.choiceSwapFromId;
                                  return (
                                    <button
                                      key={oid}
                                      disabled={alreadyUsed}
                                      onClick={() =>
                                        updateChoiceSwapFrom(
                                          actualIdx,
                                          oid,
                                          isBonusPact
                                        )
                                      }
                                      className={`text-left rounded border p-2 transition-colors w-full ${
                                        alreadyUsed
                                          ? "opacity-35 cursor-not-allowed bg-muted/20"
                                          : "hover:border-violet-500/50 hover:bg-violet-500/5 cursor-pointer"
                                      }`}
                                    >
                                      <div className="flex items-center gap-1.5">
                                        <span className="font-medium text-xs">
                                          {off.name}
                                        </span>
                                        {alreadyUsed && (
                                          <Lock className="h-3 w-3 text-muted-foreground" />
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-2">
                                        {off.description}
                                      </p>
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          )}

                          {/* Step 2: pick the replacement offering */}
                          {swapFromOffering && (
                            <>
                              <div className="flex items-center gap-2">
                                <p className="text-xs text-muted-foreground">
                                  Replacing{" "}
                                  <span className="font-medium text-foreground">
                                    {swapFromOffering.name}
                                  </span>{" "}
                                  with:
                                </p>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 px-1.5 text-xs"
                                  onClick={() =>
                                    updateChoiceSwapFrom(
                                      actualIdx,
                                      pact.choiceSwapFromId!,
                                      isBonusPact
                                    )
                                  }
                                >
                                  ← Back
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                {rules.darkOfferings
                                  .filter((o) => o.id !== pact.choiceSwapFromId)
                                  .map((off) => {
                                    const isSelected =
                                      pact.choiceSwapToId === off.id;
                                    return (
                                      <button
                                        key={off.id}
                                        onClick={() =>
                                          updateChoiceSwapTo(
                                            actualIdx,
                                            off.id,
                                            isBonusPact,
                                            pactIdx
                                          )
                                        }
                                        className={`text-left rounded border p-2 transition-colors w-full ${
                                          isSelected
                                            ? "border-violet-500 bg-violet-500/15"
                                            : "hover:border-violet-500/50 hover:bg-violet-500/5 cursor-pointer"
                                        }`}
                                      >
                                        <div className="flex items-start gap-1.5">
                                          <div className="flex-1 min-w-0">
                                            <span className="font-medium text-xs">
                                              {off.name}
                                            </span>
                                            <p className="text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-2">
                                              {off.description}
                                            </p>
                                          </div>
                                          {isSelected && (
                                            <Check className="h-3.5 w-3.5 text-violet-500 shrink-0 mt-0.5" />
                                          )}
                                        </div>
                                      </button>
                                    );
                                  })}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── FULL GIFT GRID ── */}
              {isExpanded && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    {selectedGift ? (
                      <p className="text-xs text-muted-foreground">
                        Currently:{" "}
                        <span className="font-medium text-foreground">
                          {selectedGift.name}
                        </span>
                      </p>
                    ) : (
                      <p className="text-sm font-medium text-muted-foreground">
                        Choose a Gift
                      </p>
                    )}
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() =>
                          randomiseGift(
                            actualIdx,
                            isBonusPact,
                            pactIdx,
                            pact.giftId
                          )
                        }
                      >
                        <Dice5 className="h-3 w-3 mr-1" />
                        Random
                      </Button>
                      {selectedGift && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => toggleExpanded(pactIdx)}
                        >
                          <ChevronUp className="h-3 w-3 mr-1" />
                          Collapse
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {rules.otherworldlyGifts.map((gift) => {
                      const isSelected = pact.giftId === gift.id;
                      const locked =
                        !gift.unlimited &&
                        usedLimitedGiftIds.includes(gift.id) &&
                        !isSelected;

                      return (
                        <button
                          key={gift.id}
                          disabled={locked}
                          onClick={() =>
                            updateGift(
                              actualIdx,
                              isSelected ? "" : gift.id,
                              isBonusPact,
                              pactIdx
                            )
                          }
                          className={`text-left rounded-md border p-3 transition-colors w-full ${
                            isSelected
                              ? "border-primary bg-primary/10"
                              : locked
                                ? "opacity-35 cursor-not-allowed bg-muted/20"
                                : "hover:border-primary/50 hover:bg-accent cursor-pointer"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-sm">
                                  {gift.name}
                                </span>
                                {gift.unlimited && (
                                  <Badge
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    Unlimited
                                  </Badge>
                                )}
                                {!gift.unlimited && (
                                  <Badge variant="outline" className="text-xs">
                                    Limited
                                  </Badge>
                                )}
                                {locked && (
                                  <Lock className="h-3 w-3 text-muted-foreground" />
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                {gift.description}
                              </p>
                            </div>
                            {isSelected && (
                              <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── NO GIFT & NOT EXPANDED (shouldn't happen, but safety net) ── */}
              {!selectedGift && !isExpanded && (
                <p className="text-xs text-muted-foreground italic">
                  No gift selected.
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
